import React, { useMemo } from "react";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { useWatch } from "react-hook-form";
import { useLanguage } from "../../../context/LanguageContext";

import SelectInput from "../../../components/Select";
import Input from "../../../components/Input";
import { useProfileData } from "../../../apis/profileApis";

type ProductOption = {
  id: string;
  itemName: string;
  weight: number;
};

interface ShipmentItemRowProps {
  index: number;
  control: any;
  setValue: any;
  register: any;
  errors: any;
  products: ProductOption[] | undefined;
  onRemove: () => void;
  onAdd: () => void;
  isLastRow: boolean;
  lastRowQuantity: string | undefined;
  isSidebar?: boolean;
}

export default function ShipmentItemRow({
  index,
  control,
  setValue,
  register,
  errors,
  products,
  onRemove,
  onAdd,
  isLastRow,
  lastRowQuantity,
  isSidebar = false,
}: ShipmentItemRowProps) {
  const { t } = useLanguage();
  const { data: profile } = useProfileData();

  const pId = useWatch({
    control,
    name: `products[${index}].pId`,
  }) as string | undefined;

  const weight = useWatch({
    control,
    name: `products[${index}].weight`,
  }) as number | undefined;

  const productOptions = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    return (products as any[]).flatMap((p: any) => {
      const hasVariants = Array.isArray(p?.variants) && p.variants.length > 0;
      if (hasVariants) {
        return p.variants.map((v: any) => {
          const variantSku = v?.sku || p?.sku || "";
          return {
            id: v.id,
            value: v.id,
            text: variantSku,
            weight: Number(v.weight ?? p.weight ?? 0),
          };
        });
      }
      const parentSku =
        p?.sku ||
        (typeof p?.itemName === "object"
          ? p?.itemName?.en || p?.itemName?.ar || ""
          : p?.itemName || "");
      return [
        {
          id: p.id,
          value: p.id,
          text: parentSku,
          weight: Number(p.weight ?? 0),
        },
      ];
    });
  }, [products]);

  const selectedOption = productOptions.find((opt) => opt.value === pId);

  const allFormProducts = useWatch({
    control,
    name: "products",
  }) as { pId?: string }[] | undefined;

  const selectedOtherPIds = allFormProducts
    ?.map((p, i) => (i !== index ? p?.pId : null))
    .filter(Boolean) as string[] | undefined;

  const availableProducts = productOptions.filter((opt) => {
    if (opt.value === pId) return true;
    return !selectedOtherPIds?.includes(opt.value);
  });

  const selectedClientId = useWatch({
    control,
    name: "clientId",
  }) as string | undefined;

  const clientIdToUse = selectedClientId || profile?.id;

  const isAddButtonDisabled =
    !pId ||
    !lastRowQuantity ||
    !clientIdToUse ||
    (productOptions.length > 0 && (allFormProducts?.length ?? 0) >= productOptions.length);

  if (isSidebar) {
    return (
      <div className="flex items-end gap-2 w-full col-span-2">
        {/* Product Selector */}
        <div className="flex-1 min-w-0">
          <SelectInput
            name={`products[${index}].pId`}
            value={pId ?? ""}
            label={t("Select Product")}
            disabled={!clientIdToUse}
            placeholder={!clientIdToUse ? t("Select Merchant First") : t("Select Product")}
            selectOptions={availableProducts}
            onChange={(val) => {
              setValue(`products[${index}].pId`, val, {
                shouldValidate: true,
                shouldDirty: true,
              });
              const selected = productOptions.find((opt) => opt.value === val);
              if (selected) {
                setValue(`products[${index}].weight`, selected.weight, {
                  shouldValidate: true,
                });
              }
            }}
            error={errors?.products?.[index]?.pId}
            validation={{ required: t("Please select a product") }}
            className="w-full"
          />
        </div>

        {/* Quantity */}
        <div className="w-24 shrink-0">
          <Input
            name={`products[${index}].quantity`}
            disabled={!pId}
            type="number"
            title={t("Qty")}
            register={register}
            error={errors?.products?.[index]?.quantity}
            validation={{
              required: t("Quantity is required"),
              min: { value: 1, message: t("Qty ≥ 1") },
              validate: (v: any) => {
                const n = Number(v);
                return (!isNaN(n) && n > 0) || t("Invalid qty");
              },
            }}
            className="w-full"
          />
        </div>

        {/* Remove Button */}
        <button
          type="button"
          onClick={onRemove}
          className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-yellow-100 text-black border border-yellow-400 hover:bg-yellow-200 transition-all shrink-0 mb-[1px]"
          title={t("Remove Item")}
        >
          <IconMinus size={18} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-end gap-2 col-span-2">
      {/* Product Selector */}
      <SelectInput
        name={`products[${index}].pId`}
        value={pId ?? ""}
        label={t("Select Product")}
        disabled={!clientIdToUse}
        placeholder={!clientIdToUse ? t("Select Merchant First") : t("Select Product")}
        selectOptions={availableProducts}
        onChange={(val) => {
          setValue(`products[${index}].pId`, val, {
            shouldValidate: true,
            shouldDirty: true,
          });
          const selected = productOptions.find((opt) => opt.value === val);
          if (selected) {
            setValue(`products[${index}].weight`, selected.weight, {
              shouldValidate: true,
            });
          }
        }}
        error={errors?.products?.[index]?.pId}
        validation={{ required: t("Please select a product") }}
        className="w-full"
      />

      {/* Quantity */}
      <Input
        name={`products[${index}].quantity`}
        disabled={!pId}
        type="number"
        title={t("Qty")}
        register={register}
        error={errors?.products?.[index]?.quantity}
        validation={{
          required: t("Quantity is required"),
          min: { value: 1, message: t("Qty ≥ 1") },
          validate: (v: any) => {
            const n = Number(v);
            return (!isNaN(n) && n > 0) || t("Invalid quantity");
          },
        }}
        className="w-28"
      />

      {/* Weight display */}
      <div className="w-28 text-sm text-gray-700">
        <div className="text-xs text-gray-500">{t("Weight (kg)")}</div>
        <div className="border border-gray-300 rounded-full px-3 py-2 bg-gray-50 text-center">
          {weight ?? selectedOption?.weight ?? 0}
        </div>
      </div>

      {/* Add / Remove Button */}
      {isLastRow ? (
        <button
          type="button"
          disabled={isAddButtonDisabled}
          onClick={onAdd}
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-yellow-100 text-black border border-yellow-400 transition hover:bg-yellow-200 ${!isAddButtonDisabled ? "" : "opacity-70 cursor-not-allowed"}`}
        >
          <IconPlus size={20} />
        </button>
      ) : (
        <button
          type="button"
          onClick={onRemove}
          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-red-100 text-red-600 border border-red-400 transition hover:bg-red-200"
        >
          <IconMinus size={20} />
        </button>
      )}
    </div>
  );
}
